import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/app/providers/AuthProvider";
import { useToast } from "@/components/ui/use-toast";
import type { CommentParty } from "@/features/orders/api/ordersV4Api";
import { useShipmentComments } from "@/features/orders/hooks/useOrdersV4";
import { isAdminRole } from "@/shared/utils/roles";

/** Mounted once at the Problem Solve section level (ProblemSolveSectionOutlet),
 *  not inside ProblemSolveConversationCard, so the "you were tagged" snackbar
 *  keeps working across the whole section — including after navigating back
 *  to the queue list — rather than stopping the moment a shipment's own
 *  workspace page unmounts. Per direct instruction, this follows the
 *  LAST-VIEWED shipment only (whichever :waybill route you most recently had
 *  open), not every shipment in Problem Solve at once — watching everything
 *  would need a backend capability that doesn't exist (no endpoint reports
 *  new comments across every open shipment, only per-shipment). Renders
 *  nothing; purely a side-effect component. */
export function ProblemSolveNotifier() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { waybill: routeWaybill } = useParams<{ waybill?: string }>();
  const viewerParty: CommentParty = isAdminRole(user?.role) ? "breaze" : "merchant";

  // Sticky across navigation: updates whenever a :waybill route is open,
  // stays put (keeps watching that shipment) once you navigate back to the
  // queue list, where there's no :waybill at all.
  const lastWaybillRef = useRef<string | null>(null);
  if (routeWaybill) lastWaybillRef.current = routeWaybill;
  const waybill = lastWaybillRef.current;

  // Per direct instruction, this no longer polls at all — refetchInterval:
  // false also turns off refetchOnWindowFocus/Reconnect/Mount (see
  // useShipmentComments' own manualOnly flag), so this only ever fetches
  // once: the first time the Problem Solve queue list mounts (cold cache,
  // which a refetchOnMount:false setting doesn't skip), or after a real
  // page refresh clears that cache. Still paused entirely while
  // routeWaybill matches (i.e. the shipment's own workspace page, with
  // ProblemSolveConversationCard's thread, is the page currently open):
  // that thread shares this exact query's cache key and is manual-refresh-
  // only per earlier direct instruction. The practical effect of no longer
  // polling: this won't notice a new tagged comment that arrives while
  // you're already sitting on the queue list — only on your next visit or
  // refresh, same as every other Problem Solve/Address Validation call.
  const { data } = useShipmentComments(
    { waybill: waybill ?? undefined },
    { refetchInterval: false, enabled: !routeWaybill }
  );
  const comments = data?.comments ?? [];

  // `null` means "haven't seen a first load for this waybill yet" — that
  // load seeds the set without toasting (existing history, not a new
  // arrival), and resets whenever the watched waybill itself changes so
  // switching to a different shipment doesn't flood toasts for history it
  // has never seen before.
  const seenCommentIds = useRef<Set<string> | null>(null);
  const seenForWaybillRef = useRef<string | null>(null);
  useEffect(() => {
    if (seenForWaybillRef.current !== waybill) {
      seenCommentIds.current = null;
      seenForWaybillRef.current = waybill;
    }
  }, [waybill]);
  useEffect(() => {
    if (!waybill) return;
    const seen = seenCommentIds.current;
    if (seen === null) {
      seenCommentIds.current = new Set(comments.map((c) => c.id));
      return;
    }
    for (const comment of comments) {
      if (seen.has(comment.id)) continue;
      if (comment.tags.includes(viewerParty) && comment.author_party !== viewerParty) {
        toast({
          title: `New message from ${comment.author || "Unknown"}`,
          description: comment.comment,
          variant: "info",
          duration: 3000,
        });
      }
    }
    seenCommentIds.current = new Set(comments.map((c) => c.id));
  }, [comments, viewerParty, waybill, toast]);

  return null;
}
